export const APPLICATION_CODES = {
  LIVE_CONNECT: "LIVE_CONNECT",
  AGENT_LAB: "AGENT_LAB",
  DATALAKE: "DATALAKE",
  APP_STUDIO: "APP_STUDIO",
  MARKETPLACE: "MARKETPLACE",
} as const;

const productApplicationCodes: Record<string, string> = {
  "art-live": APPLICATION_CODES.LIVE_CONNECT,
  "art-agent": APPLICATION_CODES.AGENT_LAB,
  "art-app-studio": APPLICATION_CODES.APP_STUDIO,
  "art-marketplace": APPLICATION_CODES.MARKETPLACE,
  "art-datalake": APPLICATION_CODES.DATALAKE,
};

export function applicationCodeForPath(pathname = window.location.pathname): string {
  if (pathname.startsWith("/agent-builder")) return APPLICATION_CODES.AGENT_LAB;
  if (pathname.startsWith("/app-studio")) return APPLICATION_CODES.APP_STUDIO;
  if (pathname.startsWith("/marketplace")) return APPLICATION_CODES.MARKETPLACE;
  if (pathname.startsWith("/live")) return APPLICATION_CODES.LIVE_CONNECT;
  if (pathname.startsWith("/datalake")) return APPLICATION_CODES.DATALAKE;

  return (
    localStorage.getItem("application_code") ||
    productApplicationCodes[localStorage.getItem("app_type") || ""] ||
    APPLICATION_CODES.LIVE_CONNECT
  );
}

export function currentApplication(productKey: string): string {
  const applicationCode =
    productApplicationCodes[productKey] || APPLICATION_CODES.LIVE_CONNECT;
  localStorage.setItem("app_type", productKey);
  localStorage.setItem("application_code", applicationCode);
  return applicationCode;
}

export function selectApplicationLicense(
  tenantInfo: any,
  applicationCode = applicationCodeForPath(),
): any {
  const licenses = Array.isArray(tenantInfo?.licenses) ? tenantInfo.licenses : [];
  const normalizedCode = applicationCode.toUpperCase();
  const matchesApplication = (license: any) =>
    String(license?.application?.code || "").toUpperCase() === normalizedCode ||
    license?.applications?.some(
      (application: any) =>
        String(application?.code || "").toUpperCase() === normalizedCode,
    );

  const matching = licenses.filter(matchesApplication);
  if (matching.length) {
    return matching.find((license: any) => license?.is_active) || matching[0];
  }

  const hasScopedLicenses = licenses.some((license: any) =>
    Boolean(license?.application?.code) ||
    (Array.isArray(license?.applications) && license.applications.length > 0),
  );
  if (hasScopedLicenses) return null;

  // Legacy single-plan profiles did not carry application metadata.
  return licenses.find((license: any) => license?.is_active) ||
    licenses[0] ||
    (tenantInfo?.license_plan_code ? tenantInfo : null);
}

export function licenseStateFromProfile(data: any, applicationCode?: string) {
  const tenantInfo = data?.tenant_info;
  const selectedLicense = selectApplicationLicense(tenantInfo, applicationCode);
  const licenseConfig = selectedLicense
    ? {
        ...selectedLicense,
        licenses: tenantInfo?.licenses || [],
        number_of_projects: tenantInfo?.number_of_projects,
      }
    : null;

  return {
    license: selectedLicense?.license_plan_name || selectedLicense?.license_plan_code || null,
    licenseConfig,
    hardLimit: data?.tenant?.hard_limit,
  };
}

export function isSandboxLicense(license: any): boolean {
  const code = String(license?.license_plan_code || "").toUpperCase();
  return code === "SANDBOX" || code.endsWith("_SANDBOX");
}

const humanizeCode = (code: string): string =>
  code
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char: string) => char.toUpperCase());

export function getPlanTierLabel(license: any): string {
  const rawLabel =
    license?.license_plan_name ||
    (license?.license_plan_code ? humanizeCode(license.license_plan_code) : "") ||
    license?.application?.name ||
    "";

  const categoryCode = license?.application?.code;
  if (!rawLabel || !categoryCode) return rawLabel;

  const appLabel = humanizeCode(String(categoryCode));
  if (appLabel && rawLabel.toLowerCase().startsWith(appLabel.toLowerCase())) {
    const tier = rawLabel.slice(appLabel.length).trim();
    if (tier) return tier;
  }

  return rawLabel;
}
